<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, onMounted, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import ContactAPI from 'dashboard/api/contacts';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';

const router = useRouter();
const store = useStore();
const inboxes = useMapGetter('inboxes/getAllInboxes');

const csvFile = ref(null);
const mediaArchiveFile = ref(null);
const csvFileName = ref('');
const mediaArchiveFileName = ref('');
const isValidating = ref(false);
const isStarting = ref(false);
const validationErrors = ref([]);
const validationSummary = ref({
  conversations_count: 0,
  contacts_count: 0,
  messages_count: 0,
  media_count: 0,
  failed_records: 0,
});
const csvColumns = ref([
  'conversation_id',
  'contact_phone',
  'contact_name',
  'message_content',
  'message_direction',
  'message_created_at',
  'media_url',
]);

const state = reactive({
  currentStep: 0,
  targetInboxId: '',
  sourceSystem: 'generic',
  contactStrategy: 'create_and_update',
  conversationStrategy: 'group_by_external_id',
  mediaStrategy: 'url_or_zip',
  mediaBaseUrl: '',
  createMissingContacts: true,
  preserveTimestamps: true,
  mapping: {
    conversationExternalId: 'conversation_id',
    contactName: 'contact_name',
    contactPhone: 'contact_phone',
    contactEmail: '',
    messageContent: 'message_content',
    messageDirection: 'message_direction',
    messageCreatedAt: 'message_created_at',
    mediaUrl: 'media_url',
    mediaFileName: '',
  },
});

const steps = [
  { label: 'Arquivo', icon: 'i-lucide-file-up' },
  { label: 'Colunas', icon: 'i-lucide-waypoints' },
  { label: 'Mídia', icon: 'i-lucide-paperclip' },
  { label: 'Revisão', icon: 'i-lucide-list-checks' },
];

const sourceSystems = [
  { value: 'generic', label: 'CSV genérico' },
  { value: 'zendesk', label: 'Zendesk' },
  { value: 'freshdesk', label: 'Freshdesk' },
  { value: 'intercom', label: 'Intercom' },
  { value: 'whatsapp', label: 'Backup WhatsApp' },
];

const contactStrategies = [
  { value: 'create_and_update', label: 'Criar novos e atualizar existentes' },
  { value: 'create_only', label: 'Criar apenas contatos novos' },
  { value: 'skip_unknown', label: 'Ignorar contatos não encontrados' },
];

const conversationStrategies = [
  { value: 'group_by_external_id', label: 'Agrupar por ID da conversa' },
  { value: 'group_by_contact_day', label: 'Agrupar por contato e dia' },
  { value: 'single_per_contact', label: 'Uma conversa por contato' },
];

const mediaStrategies = [
  { value: 'url_or_zip', label: 'URL no CSV ou arquivo ZIP' },
  { value: 'url_only', label: 'Usar URLs do CSV' },
  { value: 'zip_only', label: 'Resolver mídia por nome dentro de ZIP' },
  { value: 'skip_media', label: 'Importar somente texto' },
];

const requiredFields = [
  {
    key: 'conversationExternalId',
    label: 'ID externo da conversa',
    required: true,
  },
  { key: 'contactName', label: 'Nome do contato', required: false },
  { key: 'contactPhone', label: 'Telefone do contato', required: true },
  { key: 'contactEmail', label: 'E-mail do contato', required: false },
  { key: 'messageContent', label: 'Texto da mensagem', required: false },
  { key: 'messageDirection', label: 'Direção', required: true },
  { key: 'messageCreatedAt', label: 'Data/hora', required: true },
  { key: 'mediaUrl', label: 'URL da mídia', required: false },
  { key: 'mediaFileName', label: 'Nome do arquivo no ZIP', required: false },
];

const sampleRows = [
  {
    conversation_id: 'legacy-1001',
    contact_phone: '+5511999999999',
    contact_name: 'Maria Souza',
    message_content: 'Olá, preciso de ajuda',
    message_direction: 'incoming',
    message_created_at: '2026-07-01T14:32:00-03:00',
    media_url: '',
  },
  {
    conversation_id: 'legacy-1001',
    contact_phone: '+5511999999999',
    contact_name: 'Maria Souza',
    message_content: '',
    message_direction: 'outgoing',
    message_created_at: '2026-07-01T14:35:00-03:00',
    media_url: 'midias/legacy-1001/audio-01.ogg',
  },
];

const inboxOptions = computed(() =>
  (inboxes.value || []).map(inbox => ({ value: inbox.id, label: inbox.name }))
);

const columnOptions = computed(() => [
  { value: '', label: 'Não mapear' },
  ...csvColumns.value.map(column => ({ value: column, label: column })),
]);

const mappedRequiredCount = computed(
  () =>
    requiredFields.filter(field => !field.required || state.mapping[field.key])
      .length
);

const requiredTotal = computed(() => requiredFields.length);

const canReview = computed(
  () =>
    csvFile.value &&
    state.targetInboxId &&
    requiredFields
      .filter(field => field.required)
      .every(field => state.mapping[field.key])
);

const formatNumber = value => new Intl.NumberFormat('pt-BR').format(value || 0);

const importSummary = computed(() => [
  {
    label: 'Conversas estimadas',
    value: formatNumber(validationSummary.value.conversations_count),
  },
  {
    label: 'Contatos novos',
    value: formatNumber(validationSummary.value.contacts_count),
  },
  {
    label: 'Mensagens',
    value: formatNumber(validationSummary.value.messages_count),
  },
  {
    label: 'Mídias encontradas',
    value: formatNumber(validationSummary.value.media_count),
  },
]);

const parseCsvHeader = text => {
  const firstLine = text.split(/\r?\n/).find(line => line.trim());
  if (!firstLine) return;
  csvColumns.value = firstLine
    .split(',')
    .map(column => column.trim().replace(/^"|"$/g, ''))
    .filter(Boolean);
};

const onCsvSelected = event => {
  const [file] = event.target.files || [];
  if (!file) return;
  csvFile.value = file;
  csvFileName.value = file.name;
  validationErrors.value = [];
  const reader = new FileReader();
  reader.onload = () => parseCsvHeader(String(reader.result || ''));
  reader.readAsText(file.slice(0, 4096));
};

const onMediaArchiveSelected = event => {
  const [file] = event.target.files || [];
  if (!file) return;
  mediaArchiveFile.value = file;
  mediaArchiveFileName.value = file.name;
};

const appendMigrationPayload = formData => {
  formData.append('import_file', csvFile.value);
  if (mediaArchiveFile.value)
    formData.append('media_archive', mediaArchiveFile.value);
  formData.append('inbox_id', state.targetInboxId);
  formData.append('source_system', state.sourceSystem);
  formData.append('contact_strategy', state.contactStrategy);
  formData.append('conversation_strategy', state.conversationStrategy);
  formData.append('media_strategy', state.mediaStrategy);
  formData.append('media_base_url', state.mediaBaseUrl);
  formData.append('create_missing_contacts', state.createMissingContacts);
  formData.append('preserve_timestamps', state.preserveTimestamps);
  Object.entries(state.mapping).forEach(([key, value]) => {
    formData.append(`mapping[${key}]`, value || '');
  });
  return formData;
};

const validateMigration = async () => {
  if (!canReview.value) return;
  isValidating.value = true;
  try {
    const { data } = await ContactAPI.validateConversationMigration(
      appendMigrationPayload(new FormData())
    );
    validationSummary.value = data.payload || {};
    validationErrors.value = data.payload?.errors || [];
    useAlert('Arquivo validado com sucesso.');
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Não foi possível validar o arquivo.'
    );
  } finally {
    isValidating.value = false;
  }
};

const startMigration = async () => {
  if (!canReview.value) return;
  isStarting.value = true;
  try {
    const { data } = await ContactAPI.startConversationMigration(
      appendMigrationPayload(new FormData())
    );
    validationSummary.value = data.payload || validationSummary.value;
    useAlert(
      'Migração iniciada. Acompanhe o processamento no histórico de importações.'
    );
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Não foi possível iniciar a migração.'
    );
  } finally {
    isStarting.value = false;
  }
};

const downloadSampleCsv = () => {
  const headers = Object.keys(sampleRows[0]);
  const csv = [
    headers.join(','),
    ...sampleRows.map(row =>
      headers.map(header => `"${String(row[header] || '')}"`).join(',')
    ),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'modelo-migracao-conversas.csv';
  link.click();
  URL.revokeObjectURL(url);
};

const goToStep = step => {
  state.currentStep = step;
};

onMounted(async () => {
  if (!inboxes.value?.length) await store.dispatch('inboxes/get');
  if (!state.targetInboxId && inboxOptions.value.length) {
    state.targetInboxId = inboxOptions.value[0].value;
  }
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6 py-6 bg-n-surface-1">
      <div class="flex items-center justify-between gap-3 mx-auto max-w-6xl">
        <div class="min-w-0">
          <h1 class="text-xl font-medium truncate text-n-slate-12">
            Migração de conversas
          </h1>
          <p class="mt-1 text-sm text-n-slate-11">
            Importe histórico de conversas, contatos e anexos de outro sistema.
          </p>
        </div>
        <div class="flex items-center gap-2">
          <Button
            icon="i-lucide-file-down"
            label="Baixar modelo CSV"
            color="slate"
            variant="outline"
            size="sm"
            @click="downloadSampleCsv"
          />
          <Button
            icon="i-lucide-arrow-left"
            label="Voltar"
            color="slate"
            size="sm"
            @click="router.push({ name: 'contacts_dashboard_index' })"
          />
        </div>
      </div>
    </header>

    <main class="flex-1 px-6 pb-8 overflow-y-auto">
      <div
        class="grid items-start w-full grid-cols-1 gap-6 mx-auto max-w-6xl lg:grid-cols-[15rem,1fr]"
      >
        <aside class="h-fit lg:sticky lg:top-0">
          <nav class="overflow-hidden border rounded-lg border-n-weak">
            <button
              v-for="(step, index) in steps"
              :key="step.label"
              type="button"
              class="flex items-center w-full gap-3 px-4 py-3 text-sm text-left transition-colors border-b last:border-b-0 border-n-weak"
              :class="
                state.currentStep === index
                  ? 'bg-n-brand/10 text-n-brand'
                  : 'text-n-slate-11 hover:bg-n-alpha-2'
              "
              @click="goToStep(index)"
            >
              <span class="size-4" :class="step.icon" />
              <span class="font-medium">{{ step.label }}</span>
            </button>
          </nav>
        </aside>

        <div class="flex flex-col gap-5 min-w-0">
          <section
            v-if="state.currentStep === 0"
            class="p-6 border rounded-lg border-n-weak bg-n-solid-1"
          >
            <div class="flex flex-col gap-5">
              <div>
                <h2 class="text-base font-medium text-n-slate-12">
                  Origem e arquivo
                </h2>
                <p class="mt-1 text-sm text-n-slate-11 lg:whitespace-nowrap">
                  O CSV deve conter uma linha por mensagem. Conversas são
                  remontadas pelo ID externo.
                </p>
              </div>
              <div class="grid gap-4 md:grid-cols-4">
                <label class="flex flex-col gap-2">
                  <span class="text-sm font-medium text-n-slate-12"
                    >Caixa de destino</span
                  >
                  <Select
                    v-model="state.targetInboxId"
                    :options="inboxOptions"
                    placeholder="Selecionar caixa"
                  />
                </label>
                <label class="flex flex-col gap-2">
                  <span class="text-sm font-medium text-n-slate-12"
                    >Sistema de origem</span
                  >
                  <Select
                    v-model="state.sourceSystem"
                    :options="sourceSystems"
                  />
                </label>
                <label class="flex flex-col gap-2">
                  <span class="text-sm font-medium text-n-slate-12"
                    >Contatos</span
                  >
                  <Select
                    v-model="state.contactStrategy"
                    :options="contactStrategies"
                  />
                </label>
                <label class="flex flex-col gap-2">
                  <span class="text-sm font-medium text-n-slate-12"
                    >Conversas</span
                  >
                  <Select
                    v-model="state.conversationStrategy"
                    :options="conversationStrategies"
                  />
                </label>
                <label
                  class="flex flex-col items-center justify-center gap-3 px-4 py-8 text-center border border-dashed rounded-lg cursor-pointer md:col-span-4 border-n-strong hover:bg-n-alpha-2"
                >
                  <span class="i-lucide-upload-cloud size-8 text-n-brand" />
                  <span class="text-sm font-medium text-n-slate-12">{{
                    csvFileName || 'Selecionar CSV de conversas'
                  }}</span>
                  <span class="text-xs text-n-slate-11"
                    >Cabeçalhos serão lidos para o mapeamento.</span
                  >
                  <input
                    class="hidden"
                    type="file"
                    accept=".csv,text/csv"
                    @change="onCsvSelected"
                  />
                </label>
              </div>
            </div>
          </section>

          <section
            v-if="state.currentStep === 1"
            class="p-6 border rounded-lg border-n-weak bg-n-solid-1"
          >
            <div class="flex items-start justify-between gap-4 mb-5">
              <div>
                <h2 class="text-base font-medium text-n-slate-12">
                  Mapeamento de colunas
                </h2>
                <p class="mt-1 text-sm text-n-slate-11">
                  Vincule cada campo do sistema a uma coluna do CSV importado.
                </p>
              </div>
              <span
                class="px-2 py-1 text-xs font-medium rounded-md bg-n-teal-3 text-n-teal-11"
                >{{ mappedRequiredCount }}/{{ requiredTotal }}</span
              >
            </div>
            <div class="overflow-hidden border rounded-lg border-n-weak">
              <div
                v-for="field in requiredFields"
                :key="field.key"
                class="grid items-center gap-3 px-4 py-3 border-b md:grid-cols-[1fr,16rem] last:border-b-0 border-n-weak"
              >
                <div
                  class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
                >
                  {{ field.label }}
                  <span
                    v-if="field.required"
                    class="px-1.5 py-0.5 text-[0.6875rem] rounded bg-n-amber-3 text-n-amber-11"
                    >obrigatório</span
                  >
                </div>
                <Select
                  v-model="state.mapping[field.key]"
                  :options="columnOptions"
                  placeholder="Selecionar coluna"
                />
              </div>
            </div>
          </section>

          <section
            v-if="state.currentStep === 2"
            class="p-6 border rounded-lg border-n-weak bg-n-solid-1"
          >
            <div class="grid gap-5 md:grid-cols-2">
              <div>
                <h2 class="text-base font-medium text-n-slate-12">
                  Estratégia para mídia
                </h2>
                <p class="mt-1 text-sm text-n-slate-11">
                  Áudios, imagens, vídeos e documentos podem vir por URL
                  pública, caminho relativo ou ZIP.
                </p>
              </div>
              <div class="flex flex-col gap-4">
                <label class="flex flex-col gap-2">
                  <span class="text-sm font-medium text-n-slate-12"
                    >Resolução dos arquivos</span
                  >
                  <Select
                    v-model="state.mediaStrategy"
                    :options="mediaStrategies"
                  />
                </label>
                <label class="flex flex-col gap-2">
                  <span class="text-sm font-medium text-n-slate-12"
                    >URL base opcional</span
                  >
                  <input
                    v-model="state.mediaBaseUrl"
                    type="url"
                    placeholder="https://cdn.cliente.com/backups/"
                    class="w-full h-10 px-3 text-sm border-0 rounded-lg outline outline-1 -outline-offset-1 outline-n-slate-5 bg-n-surface-1 text-n-slate-12 focus:outline-n-brand"
                  />
                </label>
                <label
                  class="flex flex-col items-center justify-center gap-2 px-4 py-5 text-center border border-dashed rounded-lg cursor-pointer border-n-strong hover:bg-n-alpha-2"
                >
                  <span class="i-lucide-file-archive size-6 text-n-brand" />
                  <span class="text-sm font-medium text-n-slate-12">{{
                    mediaArchiveFileName || 'Selecionar ZIP de mídias opcional'
                  }}</span>
                  <input
                    class="hidden"
                    type="file"
                    accept=".zip,application/zip"
                    @change="onMediaArchiveSelected"
                  />
                </label>
                <div class="grid gap-3">
                  <label class="flex items-center gap-3 text-sm text-n-slate-12"
                    ><input
                      v-model="state.createMissingContacts"
                      type="checkbox"
                    />Criar contatos que não existem</label
                  >
                  <label class="flex items-center gap-3 text-sm text-n-slate-12"
                    ><input
                      v-model="state.preserveTimestamps"
                      type="checkbox"
                    />Preservar data e hora original das mensagens</label
                  >
                </div>
              </div>
            </div>
          </section>

          <section
            v-if="state.currentStep === 3"
            class="p-6 border rounded-lg border-n-weak bg-n-solid-1"
          >
            <div class="flex items-start justify-between gap-4">
              <div>
                <h2 class="text-base font-medium text-n-slate-12">
                  Revisão da migração
                </h2>
                <p class="mt-1 text-sm text-n-slate-11">
                  Valide o arquivo antes de iniciar. O processamento final roda
                  em segundo plano.
                </p>
              </div>
              <span
                class="px-2 py-1 text-xs font-medium rounded-md"
                :class="
                  canReview
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-ruby-3 text-n-ruby-11'
                "
                >{{ canReview ? 'Pronto' : 'Pendente' }}</span
              >
            </div>
            <div class="grid gap-3 mt-6 sm:grid-cols-2 lg:grid-cols-4">
              <div
                v-for="item in importSummary"
                :key="item.label"
                class="p-4 border rounded-lg border-n-weak"
              >
                <div class="text-xs text-n-slate-11">{{ item.label }}</div>
                <div class="mt-1 text-xl font-semibold text-n-slate-12">
                  {{ item.value }}
                </div>
              </div>
            </div>
            <div
              v-if="validationSummary.failed_records"
              class="mt-4 text-sm text-n-ruby-11"
            >
              {{ validationSummary.failed_records }} linha(s) com erro na
              validação.
            </div>
            <div
              v-if="validationErrors.length"
              class="mt-3 overflow-hidden text-xs border rounded-lg border-n-weak text-n-slate-11"
            >
              <div
                v-for="(error, index) in validationErrors"
                :key="index"
                class="px-3 py-2 border-b last:border-b-0 border-n-weak"
              >
                {{ error.errors }}
              </div>
            </div>
            <div class="flex justify-end gap-2 mt-6">
              <Button
                label="Validar arquivo"
                icon="i-lucide-shield-check"
                color="slate"
                variant="outline"
                :disabled="!canReview"
                :is-loading="isValidating"
                @click="validateMigration"
              />
              <Button
                label="Iniciar migração"
                icon="i-lucide-play"
                :disabled="!canReview"
                :is-loading="isStarting"
                @click="startMigration"
              />
            </div>
          </section>

          <section class="p-5 border rounded-lg border-n-weak bg-n-alpha-1">
            <h2 class="text-sm font-medium text-n-slate-12">
              Contrato recomendado para mídia
            </h2>
            <div class="grid gap-3 mt-3 text-sm text-n-slate-11 md:grid-cols-3">
              <div>
                <span class="font-medium text-n-slate-12">CSV</span>
                <p class="mt-1">
                  `media_url` aceita URL absoluta ou caminho relativo como
                  `midias/audio-01.ogg`.
                </p>
              </div>
              <div>
                <span class="font-medium text-n-slate-12">ZIP</span>
                <p class="mt-1">
                  O backend procura o arquivo pelo caminho informado no CSV e
                  anexa à mensagem.
                </p>
              </div>
              <div>
                <span class="font-medium text-n-slate-12">Falhas</span>
                <p class="mt-1">
                  Mensagens importam mesmo sem mídia; anexos faltantes entram no
                  relatório.
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </main>
  </section>
</template>
